import React, { useMemo, useState, useEffect } from 'react';
import {
  Text,
  Paper,
  Box,
  Loader,
  Alert,
  Tabs,
  Card,
  Anchor,
  Center,
  Group,
  Stack,
  Switch,
  Button,
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { IconDownload } from '@tabler/icons-react';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../../hooks/useAuthenticatedData';
import config from '../../../../config';
import MembershipPopup from '../MembershipPopup';
import SQLEditorPopup from '../SQLEditorPopup';

const ColorLegend = () => (
  <Card shadow="sm" padding="xs" radius="sm" withBorder>
    <Text size="sm" fw={500} mb={4}>
      Legend
    </Text>
    <Stack gap={4}>
      {[
        { color: 'var(--mantine-color-green-6)', label: 'Margin ≥ 20%' },
        { color: 'var(--mantine-color-yellow-6)', label: 'Margin between 15% and 20%' },
        { color: 'var(--mantine-color-red-6)', label: 'Margin < 15%' },
      ].map(({ color, label }) => (
        <Group key={label} gap={6} align="center">
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: color,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Group>
      ))}
    </Stack>
  </Card>
);

function PurchasingDepartmentInventoryLandedSpendDetails() {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const period = searchParams.get('period')?.toUpperCase() || 'MTD';
  const filterParam = searchParams.get('filter')?.toUpperCase();

  // Initialize state based on URL parameter, defaulting to true if no filter is specified
  const [onlyPurchasingWarehouses, setOnlyPurchasingWarehouses] = useState(() => {
    if (filterParam) {
      return filterParam === 'IN';
    }
    return true; // Default to true when no filter parameter is present
  });

  // State for pop-up dialogs
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);

  // Update state when URL changes
  useEffect(() => {
    const newFilterParam = searchParams.get('filter')?.toUpperCase();
    if (newFilterParam) {
      setOnlyPurchasingWarehouses(newFilterParam === 'IN');
    }
  }, [location.search]);

  // Generate the filter value based on checkbox state
  const warehouseFilter = onlyPurchasingWarehouses ? 'IN' : 'OUT';

  // Build URL with both period and filter parameters
  const url = `${config.apiBaseUrl}/supply-chain/purchasing/department/scorecard/inventory-landed-spend-details?period=${period}&filter=${warehouseFilter}`;
  const { data, isLoading, error } = useAuthenticatedData(url);

  const handleTabChange = (newPeriod) => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('period', newPeriod);
    newSearchParams.set('filter', warehouseFilter);
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  const handleFilterChange = (event) => {
    const newValue = event.currentTarget.checked;
    setOnlyPurchasingWarehouses(newValue);
    
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('filter', newValue ? 'IN' : 'OUT');
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  const rows = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item, index) => ({
      id: index,
      // Extract the values from the nested structure
      ...item.values,
    }));
  }, [data]);

  const generateExportFileName = () => `Purchasing-${period}-inventory-landed-spend-details`;

  // CSV export configuration
  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: generateExportFileName(),
  });

  const handleExportData = () => {
    if (!rows.length) return;
    
    // Format data for export with proper column names
    const exportData = rows.map(row => ({
      'Entered': row.entered ? new Date(row.entered).toLocaleDateString() : '',
      'Part Number': row.partnumber || '',
      'Condition': row.condition || '',
      'Vendor': row.accountname || '',
      'Buyer': row.fullname || '',
      'Qty': row.qtyordered || '',
      'Unit Price': row.unitprice || '',
      'Unit Sell Price': row.unitsellprice || '',
      'Spend': row.spend || '',
      'Projected Revenue': row.projectedrev || '',
      'Projected Margin': row.projectedmargin || '',
      'Margin %': row.marginpercent ? `${Number(row.marginpercent).toFixed(2)}%` : '',
      'Pentagon Doc#': row.docno || '',
      'Line': row.userline || '',
      'Doc Type': row.doctype || '',
    }));

    const csv = generateCsv(csvConfig)(exportData);
    download(csvConfig)(csv);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'entered',
        header: 'Entered',
        size: 150,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      { accessorKey: 'partnumber', header: 'Part Number', size: 150 },
      { accessorKey: 'condition', header: 'Condition', size: 100 },
      { accessorKey: 'accountname', header: 'Vendor', size: 150 },
      { accessorKey: 'fullname', header: 'Buyer', size: 150 },
      { accessorKey: 'qtyordered', header: 'Qty', size: 80 },
      {
        accessorKey: 'unitprice',
        header: 'Unit Price',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'unitsellprice',
        header: 'Unit Sell Price',
        size: 140,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'spend',
        header: 'Spend',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'projectedrev',
        header: 'Projected Revenue',
        size: 160,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'projectedmargin',
        header: 'Projected Margin',
        size: 160,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'marginpercent',
        header: 'Margin %',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toFixed(2)}%` : 'N/A',
      },
      { accessorKey: 'docno', header: 'Pentagon Doc#', size: 120 },
      { accessorKey: 'userline', header: 'Line', size: 80 },
      { accessorKey: 'doctype', header: 'Doc Type', size: 140 },
    ],
    []
  );

  const getRowStyle = (row) => {
    const margin = row.original.marginpercent;
    if (margin === null || margin === undefined || isNaN(margin)) return {};
    
    if (margin >= 20) {
      return {
        backgroundColor: 'var(--mantine-color-green-1)',
        color: 'var(--mantine-color-green-9)',
      };
    }
    if (margin >= 15 && margin < 20) {
      return {
        backgroundColor: 'var(--mantine-color-yellow-1)',
        color: 'var(--mantine-color-yellow-9)',
      };
    }
    if (margin < 15) {
      return {
        backgroundColor: 'var(--mantine-color-red-1)',
        color: 'var(--mantine-color-red-9)',
      };
    }
    return {};
  };

  const table = useMantineReactTable({
    columns,
    data: rows,
    enableGlobalFilter: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enablePagination: true,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    initialState: {
      density: 'xs',
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    mantineTableBodyRowProps: ({ row }) => ({
      style: getRowStyle(row),
    }),
    mantineTableProps: {
      style: { fontSize: '0.875rem' },
    },
    mantineBottomToolbarProps: {
      style: { padding: '8px' },
    },
    renderTopToolbarCustomActions: () => (
      <Box style={{ display: 'flex', gap: '8px', padding: '8px' }}>
        <Button
          onClick={handleExportData}
          leftSection={<IconDownload size={16} />}
          variant="filled"
          size="sm"
          disabled={rows.length === 0}
        >
          Export All Data
        </Button>
      </Box>
    ),
  });

  return (
    <Paper shadow="sm" style={{ maxWidth: '100%' }}>
      {/* Header with title and checkbox */}
      <Group justify="space-between" align="center" mb="md">
        <Text
          component="h1"
          style={{
            fontFamily: 'Orbitron, sans-serif',
            color: '#FDB813',
            fontStyle: 'italic',
            fontSize: '1.5rem',
            marginBottom: 0,
            textAlign: 'left'
          }}
        >
          Purchasing Scorecard - Inventory Landed Spend Details
        </Text>
        
        <Switch
          label="Only purchasing warehouses"
          checked={onlyPurchasingWarehouses}
          onChange={handleFilterChange}
          size="sm"
        />
      </Group>

      {/* Technical options: small links for debugging */}
      <Group mb="sm" gap="sm">
  
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=purchasing_dept_landed_inventory_spend_summary',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Summary Query
        </Anchor>
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=purchasing_dept_landed_inventory_spend_details',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Details Query
        </Anchor>
      </Group>

      {/* About the Data and Legend Block */}
      <Group mb="sm" align="stretch" grow>
        <Card shadow="sm" padding="xs" radius="sm" withBorder>
          <Text size="sm" fw={500} mb={4}>
            About the Data
          </Text>
          <Text size="xs" c="dimmed">
            These are purchase receivers for parts we've purchased and that have arrived. ITS doesn't incur a cost until the parts arrive.
            The current Pentagon sell price is used to calculate the projected revenue and margin. 
            These receivers are limited to purchase orders created by the purchasing department, who you can see by clicking the "Who is Included?" link above.
          </Text>
        </Card>
        <ColorLegend />
      </Group>

      <Box mt="sm">
        <Tabs value={period} onChange={handleTabChange} mb="sm">
          <Tabs.List>
            <Tabs.Tab value="MTD">Month-to-Date</Tabs.Tab>
            <Tabs.Tab value="QTD">Quarter-to-Date</Tabs.Tab>
            <Tabs.Tab value="YTD">Year-to-Date</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </Box>

      <Paper shadow="sm" p="sm" style={{ height: 600 }}>
        <Box style={{ height: '100%' }}>
          {isLoading ? (
            <Center style={{ height: '100%' }}>
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">Loading...</Text>
              </Group>
            </Center>
          ) : error ? (
            <Alert color="red" title="Error">
              {error.message}
            </Alert>
          ) : (
            <MantineReactTable table={table} />
          )}
        </Box>
      </Paper>
 
    </Paper>
  );
}

export default PurchasingDepartmentInventoryLandedSpendDetails;